"use client";

import { c as _c } from "react/compiler-runtime";
import type { CommunityMemberMinimal } from "@/entities/IProfile";
import type { ApiIdentity } from "@/generated/models/ApiIdentity";
import { areEqualAddresses } from "@/helpers/Helpers";
import { getUserProfile } from "@/helpers/server.helpers";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useIdentity } from "@/hooks/useIdentity";
import { commonApiFetch } from "@/services/api/common-api";
import { faAnglesDown, faAnglesUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { isAddress } from "viem";
import TransferModalPfp from "../nft-transfer/TransferModalPfp";
const MIN_SEARCH_LENGTH = 3;
function getSearchStatusText(isSearching: boolean, needsMoreSearchCharacters: boolean, remainingSearchCharacters: number, resultsLen: number, trimmedQuery: string): string {
  if (isSearching) return "Searching…";
  if (needsMoreSearchCharacters) {
    const plural = remainingSearchCharacters === 1 ? "" : "s";
    return `Type ${remainingSearchCharacters} more character${plural}`;
  }
  if (resultsLen > 0) {
    const plural = resultsLen === 1 ? "" : "s";
    return `Found ${resultsLen} result${plural}`;
  }
  if (trimmedQuery) return "No results.";
  return "Type to search.";
}
function RecipientSelectedDisplay(t0) {
  const $ = _c(56);
  const {
    selectedProfile,
    profile,
    isIdentityLoading,
    onClear,
    walletsListRef,
    walletsHasOverflow,
    walletsAtEnd,
    selectedWallet,
    onWalletSelect
  } = t0;
  let t1;
  if ($[0] !== profile || $[1] !== selectedProfile.display || $[2] !== selectedProfile.handle || $[3] !== selectedProfile.wallet) {
    t1 = () => {
      if (profile?.wallets && profile.wallets.length > 0) {
        return profile.wallets;
      }
      if (selectedProfile.wallet) {
        return [{
          wallet: selectedProfile.wallet,
          display: selectedProfile.display || selectedProfile.handle || selectedProfile.wallet,
          tdh: 0
        }];
      }
      return [];
    };
    $[0] = profile;
    $[1] = selectedProfile.display;
    $[2] = selectedProfile.handle;
    $[3] = selectedProfile.wallet;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const getWallets = t1;
  let wallets;
  let walletsContent;
  if ($[5] !== getWallets || $[6] !== isIdentityLoading || $[7] !== onWalletSelect || $[8] !== selectedProfile.display || $[9] !== selectedProfile.handle || $[10] !== selectedWallet) {
    wallets = getWallets();
    if (isIdentityLoading) {
      let t2;
      if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = <div className="tw-text-xs tw-opacity-60">Loading wallets…</div>;
        $[13] = t2;
      } else {
        t2 = $[13];
      }
      walletsContent = t2;
    } else {
      if (wallets.length === 0) {
        const t2 = selectedProfile.display || selectedProfile.handle;
        let t3;
        if ($[14] !== t2) {
          t3 = <div className="tw-text-xs tw-opacity-60">No wallets found for {t2}.</div>;
          $[14] = t2;
          $[15] = t3;
        } else {
          t3 = $[15];
        }
        walletsContent = t3;
      } else {
        let t2;
        if ($[16] !== onWalletSelect || $[17] !== selectedWallet) {
          t2 = w => {
            const isSel = selectedWallet?.toLowerCase() === w.wallet.toLowerCase();
            const hasDisplay = w.display && w.display.toLowerCase() !== w.wallet.toLowerCase();
            return <button key={w.wallet} type="button" onClick={() => onWalletSelect(w.wallet)} className={["tw-flex tw-min-h-[58px] tw-w-full tw-flex-col tw-rounded-lg tw-border tw-border-white/10 tw-bg-white/10 tw-p-2 hover:tw-bg-white/15", hasDisplay ? "tw-items-start tw-justify-between" : "tw-items-start tw-justify-center", isSel ? "tw-border-2 tw-border-solid !tw-border-emerald-400" : ""].join(" ")}><div className="tw-text-sm tw-font-medium">{w.display || w.wallet}</div>{hasDisplay && <div className="tw-text-[11px] tw-opacity-60">{w.wallet}</div>}</button>;
          };
          $[16] = onWalletSelect;
          $[17] = selectedWallet;
          $[18] = t2;
        } else {
          t2 = $[18];
        }
        walletsContent = wallets.map(t2);
      }
    }
    $[5] = getWallets;
    $[6] = isIdentityLoading;
    $[7] = onWalletSelect;
    $[8] = selectedProfile.display;
    $[9] = selectedProfile.handle;
    $[10] = selectedWallet;
    $[11] = wallets;
    $[12] = walletsContent;
  } else {
    wallets = $[11];
    walletsContent = $[12];
  }
  const t2 = selectedProfile.display || selectedProfile.handle || selectedProfile.wallet;
  let t3;
  if ($[19] !== selectedProfile.level || $[20] !== selectedProfile.pfp || $[21] !== t2) {
    t3 = <TransferModalPfp src={selectedProfile.pfp} alt={t2} level={selectedProfile.level} />;
    $[19] = selectedProfile.level;
    $[20] = selectedProfile.pfp;
    $[21] = t2;
    $[22] = t3;
  } else {
    t3 = $[22];
  }
  const t4 = selectedProfile.handle || selectedProfile.display;
  let t5;
  if ($[23] !== t4) {
    t5 = <div className="tw-truncate tw-text-sm tw-font-medium">{t4}</div>;
    $[23] = t4;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  let t6;
  if ($[25] !== selectedProfile.tdh) {
    t6 = selectedProfile.tdh.toLocaleString();
    $[25] = selectedProfile.tdh;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  let t7;
  if ($[27] !== selectedProfile.level || $[28] !== t6) {
    t7 = <div className="tw-truncate tw-text-[11px] tw-opacity-60">TDH: {t6} - Level:{" "}{selectedProfile.level}</div>;
    $[27] = selectedProfile.level;
    $[28] = t6;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  let t8;
  if ($[30] !== t5 || $[31] !== t7) {
    t8 = <div className="tw-min-w-0">{t5}{t7}</div>;
    $[30] = t5;
    $[31] = t7;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  let t9;
  if ($[33] !== t3 || $[34] !== t8) {
    t9 = <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-3">{t3}{t8}</div>;
    $[33] = t3;
    $[34] = t8;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  let t10;
  if ($[36] !== onClear) {
    t10 = <button type="button" className="tw-rounded-md tw-border-2 tw-border-solid tw-border-[#444] tw-bg-white/10 tw-px-2 tw-py-1 !tw-text-xs tw-font-medium hover:tw-bg-white/15" onClick={onClear}>Change</button>;
    $[36] = onClear;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  let t11;
  if ($[38] !== t10 || $[39] !== t9) {
    t11 = <div className="tw-flex tw-items-center tw-justify-between tw-rounded-lg tw-bg-white/10 tw-px-3 tw-py-2">{t9}{t10}</div>;
    $[38] = t10;
    $[39] = t9;
    $[40] = t11;
  } else {
    t11 = $[40];
  }
  let t12;
  if ($[41] !== wallets.length) {
    t12 = wallets.length > 1 && <div className="tw-text-sm">Choose destination wallet</div>;
    $[41] = wallets.length;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  let t13;
  if ($[43] !== walletsContent || $[44] !== walletsListRef) {
    t13 = <div ref={walletsListRef} className="tw-[scrollbar-gutter:stable] tw-min-h-0 tw-flex-1 tw-space-y-2 tw-overflow-auto tw-pr-3 tw-scrollbar-thin tw-scrollbar-track-transparent tw-scrollbar-thumb-white/30 hover:tw-scrollbar-thumb-white/50">{walletsContent}</div>;
    $[43] = walletsContent;
    $[44] = walletsListRef;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  let t14;
  if ($[46] !== walletsAtEnd || $[47] !== walletsHasOverflow) {
    t14 = walletsHasOverflow && <div className="tw-text-center tw-text-xs tw-opacity-75"><FontAwesomeIcon icon={walletsAtEnd ? faAnglesUp : faAnglesDown} />{" "}Scroll for more</div>;
    $[46] = walletsAtEnd;
    $[47] = walletsHasOverflow;
    $[48] = t14;
  } else {
    t14 = $[48];
  }
  let t15;
  if ($[49] !== t12 || $[50] !== t13 || $[51] !== t14) {
    t15 = <div className="tw-flex tw-min-h-0 tw-flex-col tw-space-y-2 tw-pt-4">{t12}{t13}{t14}</div>;
    $[49] = t12;
    $[50] = t13;
    $[51] = t14;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  let t16;
  if ($[53] !== t11 || $[54] !== t15) {
    t16 = <>{t11}{t15}</>;
    $[53] = t11;
    $[54] = t15;
    $[55] = t16;
  } else {
    t16 = $[55];
  }
  return t16;
}
function RecipientSearchDisplay(t0) {
  const $ = _c(25);
  const {
    query,
    setQuery,
    searchStatusText,
    results,
    onPick,
    resultsListRef,
    resultsHasOverflow,
    resultsAtEnd,
    searchInputRef,
    placeholder
  } = t0;
  let t1;
  if ($[0] !== setQuery) {
    t1 = e => setQuery(e.target.value);
    $[0] = setQuery;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = placeholder ?? "Search profile by handle, ens or wallet";
  let t3;
  if ($[2] !== query || $[3] !== searchInputRef || $[4] !== t1 || $[5] !== t2) {
    t3 = <input autoFocus={true} type="text" value={query} onChange={t1} placeholder={t2} className="tw-h-14 tw-w-full tw-rounded-lg tw-border-none tw-bg-white/10 tw-px-3 tw-py-2 focus:tw-bg-white/20 focus:tw-outline-none" ref={searchInputRef} />;
    $[2] = query;
    $[3] = searchInputRef;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== searchStatusText) {
    t4 = <div className="tw-text-[12px] tw-opacity-60">{searchStatusText}</div>;
    $[7] = searchStatusText;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== onPick || $[10] !== results) {
    let t6;
    if ($[12] !== onPick) {
      t6 = r => <button key={r.profile_id ?? r.wallet} type="button" onClick={() => onPick(r)} className="tw-flex tw-w-full tw-items-center tw-gap-3 tw-rounded-lg tw-border tw-border-white/10 tw-bg-white/10 tw-p-2 tw-text-left hover:tw-bg-white/15"><TransferModalPfp src={r.pfp} alt={r.display || r.handle || r.wallet} level={r.level} /><div className="tw-min-w-0 tw-flex-1"><div className="tw-truncate tw-text-sm tw-font-medium">{r.handle || r.display}</div><div className="tw-truncate tw-text-[11px] tw-opacity-60">TDH: {r.tdh.toLocaleString()} - Level: {r.level}</div></div></button>;
      $[12] = onPick;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    t5 = results.map(t6);
    $[9] = onPick;
    $[10] = results;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[14] !== resultsListRef || $[15] !== t5) {
    t6 = <div ref={resultsListRef} className="tw-[scrollbar-gutter:stable] tw-min-h-0 tw-flex-1 tw-space-y-2 tw-overflow-auto tw-pr-3 tw-scrollbar-thin tw-scrollbar-track-transparent tw-scrollbar-thumb-white/30 hover:tw-scrollbar-thumb-white/50">{t5}</div>;
    $[14] = resultsListRef;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== resultsAtEnd || $[18] !== resultsHasOverflow) {
    t7 = resultsHasOverflow && <div className="tw-text-center tw-text-xs tw-opacity-75"><FontAwesomeIcon icon={resultsAtEnd ? faAnglesUp : faAnglesDown} />{" "}Scroll for more</div>;
    $[17] = resultsAtEnd;
    $[18] = resultsHasOverflow;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t3 || $[21] !== t4 || $[22] !== t6 || $[23] !== t7) {
    t8 = <>{t3}{t4}{t6}{t7}</>;
    $[20] = t3;
    $[21] = t4;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  return t8;
}
interface RecipientSelectorProps {
  readonly open: boolean;
  readonly selectedProfile: CommunityMemberMinimal | null;
  readonly selectedWallet: string | null;
  readonly onProfileSelect: (profile: CommunityMemberMinimal | null) => void;
  readonly onWalletSelect: (wallet: string | null) => void;
  readonly placeholder?: string;
  readonly showLabel?: boolean;
  readonly label?: string;
}
export default function RecipientSelector({
  open,
  selectedProfile,
  selectedWallet,
  onProfileSelect,
  onWalletSelect,
  placeholder,
  showLabel = true,
  label = "Recipient"
}: RecipientSelectorProps) {
  const [query, setQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<CommunityMemberMinimal[]>([]);
  const [pendingWalletMatch, setPendingWalletMatch] = useState<string | null>(null);
  const pendingMatchAttempted = useRef(false);
  const handleOrWallet = selectedProfile?.handle ?? selectedProfile?.wallet ?? null;
  const {
    profile,
    isLoading: isIdentityLoading
  } = useIdentity({
    handleOrWallet: handleOrWallet ?? "",
    initialProfile: null
  });
  const resultsListRef = useRef<HTMLDivElement | null>(null);
  const walletsListRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [resultsHasOverflow, setResultsHasOverflow] = useState(false);
  const [walletsHasOverflow, setWalletsHasOverflow] = useState(false);
  const [resultsAtEnd, setResultsAtEnd] = useState(false);
  const [walletsAtEnd, setWalletsAtEnd] = useState(false);
  const trimmedQuery = query.trim();
  const needsMoreSearchCharacters = trimmedQuery.length > 0 && trimmedQuery.length < MIN_SEARCH_LENGTH;
  const remainingSearchCharacters = Math.max(MIN_SEARCH_LENGTH - trimmedQuery.length, 0);
  useEffect(() => {
    if (!open) {
      pendingMatchAttempted.current = false;
      setQuery("");
      setIsSearching(false);
      setResults([]);
      setResultsHasOverflow(false);
      setWalletsHasOverflow(false);
      setResultsAtEnd(false);
      setWalletsAtEnd(false);
      setPendingWalletMatch(null);
    }
  }, [open]);
  useEffect(() => {
    if (!selectedProfile || isIdentityLoading || !profile?.wallets || selectedWallet) {
      return;
    }
    const wallets = profile.wallets;
    if (pendingWalletMatch) {
      const normalizedMatch = pendingWalletMatch.toLowerCase();
      const matchedWallet = wallets.find(w => w.wallet.toLowerCase() === normalizedMatch || w.display?.toLowerCase() === normalizedMatch);
      if (matchedWallet) {
        onWalletSelect(matchedWallet.wallet);
      }
      setPendingWalletMatch(null);
      return;
    }
    if (wallets.length === 1 && wallets[0] && !pendingMatchAttempted.current) {
      onWalletSelect(wallets[0].wallet);
    }
  }, [selectedProfile, isIdentityLoading, profile?.wallets, selectedWallet, pendingWalletMatch, onWalletSelect]);
  const debouncedQuery = useDebouncedValue(trimmedQuery, 350);
  const enabled = open && debouncedQuery.length >= MIN_SEARCH_LENGTH;
  const {
    data,
    isFetching
  } = useQuery<CommunityMemberMinimal[]>({
    queryKey: ["memberSearch", debouncedQuery],
    enabled,
    refetchOnWindowFocus: false,
    retry: 1,
    staleTime: 30_000,
    gcTime: 300_000,
    queryFn: async ({
      signal
    }: {
      signal: AbortSignal;
    }) => {
      try {
        const arr = await commonApiFetch<CommunityMemberMinimal[]>({
          endpoint: "community-members",
          params: {
            param: debouncedQuery
          },
          signal
        });
        if (arr && arr.length > 0) return arr;
        const identity = await getUserProfile({
          user: debouncedQuery,
          headers: {}
        });
        const communityMember: CommunityMemberMinimal = {
          profile_id: identity.id,
          handle: identity.handle,
          wallet: identity.primary_wallet,
          pfp: identity.pfp,
          display: identity.display ?? null,
          normalised_handle: identity.normalised_handle,
          primary_wallet: identity.primary_wallet,
          tdh: identity.tdh,
          level: identity.level,
          cic_rating: identity.cic
        };
        return [communityMember];
      } catch {
        return [];
      }
    }
  });
  useEffect(() => {
    const preDebounce = open && trimmedQuery.length >= MIN_SEARCH_LENGTH && debouncedQuery !== trimmedQuery;
    setIsSearching(preDebounce || enabled && isFetching);
  }, [open, trimmedQuery, debouncedQuery, enabled, isFetching]);
  useEffect(() => {
    if (!enabled) {
      setResults([]);
      return;
    }
    setResults(data ?? []);
  }, [enabled, data, setResults]);
  useEffect(() => {
    if (selectedProfile || !data || data.length === 0 || !debouncedQuery || !trimmedQuery) {
      return;
    }
    const normalizedQuery = debouncedQuery.toLowerCase();
    const isExactAddress = isAddress(debouncedQuery);
    const isLikelyEns = normalizedQuery.endsWith(".eth");
    if (!isExactAddress && !isLikelyEns) {
      return;
    }
    if (data.length === 1 && data[0]) {
      const result = data[0];
      const walletMatch = areEqualAddresses(result.wallet, debouncedQuery);
      if (isExactAddress ? walletMatch : isLikelyEns) {
        pendingMatchAttempted.current = true;
        setPendingWalletMatch(debouncedQuery);
        onProfileSelect(result);
      }
    }
  }, [data, debouncedQuery, trimmedQuery, selectedProfile, onProfileSelect]);
  useEffect(() => {
    const hasOverflow = (el: HTMLElement | null) => !!el && el.scrollHeight > el.clientHeight;
    const check = () => {
      setResultsHasOverflow(hasOverflow(resultsListRef.current));
      setWalletsHasOverflow(hasOverflow(walletsListRef.current));
      const nearBottom = (el_0: HTMLElement | null) => {
        if (!el_0) return false;
        const threshold = 4;
        return el_0.scrollTop + el_0.clientHeight >= el_0.scrollHeight - threshold;
      };
      setResultsAtEnd(nearBottom(resultsListRef.current));
      setWalletsAtEnd(nearBottom(walletsListRef.current));
    };
    check();
    globalThis.addEventListener("resize", check);
    const resultsEl = resultsListRef.current;
    const walletsEl = walletsListRef.current;
    const onScrollResults = () => {
      const el_1 = resultsListRef.current;
      if (!el_1) return;
      const threshold_0 = 4;
      setResultsAtEnd(el_1.scrollTop + el_1.clientHeight >= el_1.scrollHeight - threshold_0);
    };
    const onScrollWallets = () => {
      const el_2 = walletsListRef.current;
      if (!el_2) return;
      const threshold_1 = 4;
      setWalletsAtEnd(el_2.scrollTop + el_2.clientHeight >= el_2.scrollHeight - threshold_1);
    };
    resultsEl?.addEventListener("scroll", onScrollResults);
    walletsEl?.addEventListener("scroll", onScrollWallets);
    return () => {
      globalThis.removeEventListener("resize", check);
      resultsEl?.removeEventListener("scroll", onScrollResults);
      walletsEl?.removeEventListener("scroll", onScrollWallets);
    };
  }, [open, results, profile?.wallets, isIdentityLoading]);
  const searchStatusText = getSearchStatusText(isSearching, needsMoreSearchCharacters, remainingSearchCharacters, results.length, trimmedQuery);
  const handleClear = () => {
    pendingMatchAttempted.current = false;
    setPendingWalletMatch(null);
    onProfileSelect(null);
    onWalletSelect(null);
    setResults([]);
    setQuery("");
    setTimeout(() => searchInputRef.current?.focus(), 0);
  };
  return <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-space-y-2">
      {showLabel && <div className={`tw-font-semibold ${selectedProfile ? "tw-mb-2" : "tw-mb-0"}`}>
          {label}
        </div>}
      {selectedProfile ? <RecipientSelectedDisplay selectedProfile={selectedProfile} profile={profile} isIdentityLoading={isIdentityLoading} onClear={handleClear} walletsListRef={walletsListRef} walletsHasOverflow={walletsHasOverflow} walletsAtEnd={walletsAtEnd} selectedWallet={selectedWallet} onWalletSelect={onWalletSelect} /> : <RecipientSearchDisplay query={query} setQuery={setQuery} searchStatusText={searchStatusText} results={results} onPick={r => {
      onProfileSelect(r);
      onWalletSelect(null);
    }} resultsListRef={resultsListRef} resultsHasOverflow={resultsHasOverflow} resultsAtEnd={resultsAtEnd} searchInputRef={searchInputRef} placeholder={placeholder ?? "Search profile by handle, ens or wallet"} />}
    </div>;
}